import React, { useEffect } from "react";
import {
  Stack,
  MenuItem,
  Box,
  Chip,
  Select,
  OutlinedInput,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  alpha,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SendIcon from "@mui/icons-material/Send";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { sendNotification } from "../redux/actions/notificationActions";
import { getUsers } from "../redux/actions/userActions";
import { cleanFormData } from "../helpers/formHelpers";

import { NotificationType } from "../shared/enums/NotificationType";
import { NotificationChannel } from "../shared/enums/NotificationChannel";
import { notificationActions } from "../redux/slices/notification";

const notificationSchema = Yup.object().shape({
  type: Yup.string()
    .oneOf(Object.values(NotificationType))
    .required("Notification type is required"),
  subject: Yup.string().required("Subject line is required").max(255),
  messageBody: Yup.string().required("Message content is required").max(2000),
  channel: Yup.string()
    .oneOf(Object.values(NotificationChannel))
    .required("Delivery channel is required"),
  targetRole: Yup.string().nullable(),
  targetUserIds: Yup.array().when("targetRole", {
    is: (val: string | null) => !val || val === "",
    then: () => Yup.array().min(1, "Select at least one user or a target role"),
    otherwise: () => Yup.array().nullable(),
  }),
});

interface CreateNotificationFormProps {
  loading: boolean;
  error: string | null;
}

const CreateNotificationForm: React.FC<CreateNotificationFormProps> = ({
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { items: users } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (!users || users.length === 0) dispatch(getUsers({ size: 1000 }));
  }, [dispatch, users]);

  const initialValues = {
    targetUserIds: [],
    targetRole: "",
    type: NotificationType.SYSTEM_ALERT,
    subject: "",
    messageBody: "",
    channel: NotificationChannel.IN_APP,
    scheduledSendTime: "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(notificationActions.setErrorCreate(null))}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={notificationSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values);
          dispatch(sendNotification(payload));
        }}
      >
        {({
          values,
          setFieldValue,
          touched,
          errors,
          isValid,
          dirty,
        }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={3}>
              {/* --- TOP SECTION: DELIVERY SETTINGS (CSS GRID) --- */}
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormSelectField
                  name="channel"
                  label="Delivery Channel"
                  options={[
                    {
                      value: NotificationChannel.IN_APP,
                      label: "In-App Dashboard",
                    },
                    {
                      value: NotificationChannel.EMAIL,
                      label: "Registered Email",
                    },
                    {
                      value: NotificationChannel.SMS,
                      label: "SMS / Text Message",
                    },
                  ]}
                />
                <FormSelectField
                  name="type"
                  label="Context / Category"
                  options={[
                    {
                      value: NotificationType.SYSTEM_ALERT,
                      label: "System Alert",
                    },
                    {
                      value: NotificationType.MAINTENANCE,
                      label: "Maintenance Notice",
                    },
                    {
                      value: NotificationType.REMINDER,
                      label: "General Reminder",
                    },
                  ]}
                />
              </Box>

              {/* --- RECIPIENT SELECTION BOX --- */}
              <Box
                sx={{
                  p: 2.5,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.default, 0.4),
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "text.secondary",
                    mb: 2,
                    display: "block",
                    letterSpacing: "0.05rem",
                    textTransform: "uppercase",
                  }}
                >
                  Recipient Selection (Choose Role OR Specific Users)
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
                  }}
                >
                  <FormSelectField
                    name="targetRole"
                    label="Target Whole Role"
                    options={[
                      { value: "", label: "-- None --" },
                      { value: "SUPER_ADMIN", label: "All Super Admins" },
                      { value: "BRANCH_MANAGER", label: "All Branch Managers" },
                      { value: "CASHIER", label: "All Cashiers" },
                    ]}
                  />

                  <FormControl
                    fullWidth
                    size="small"
                    disabled={!!values.targetRole}
                    error={
                      touched.targetUserIds && Boolean(errors.targetUserIds)
                    }
                  >
                    <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>
                      Select Individual Users
                    </InputLabel>
                    <Select
                      multiple
                      value={values.targetUserIds || []}
                      onChange={(e) =>
                        setFieldValue("targetUserIds", e.target.value)
                      }
                      input={<OutlinedInput label="Select Individual Users" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(selected as string[]).map((id) => (
                            <Chip
                              key={id}
                              label={
                                users.find((u) => u.id === id)?.email || id
                              }
                              size="small"
                              sx={{ height: 24, fontSize: "0.75rem" }}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Typography sx={{ fontSize: "0.875rem" }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              ml: 1,
                              fontStyle: "italic",
                            }}
                          >
                            ({user.email})
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.targetUserIds && errors.targetUserIds && (
                      <FormHelperText sx={{ fontWeight: 500 }}>
                        {errors.targetUserIds as string}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Box>

              {/* --- MESSAGE CONTENT --- */}
              <Stack spacing={2}>
                <FormTextField
                  name="subject"
                  label="Message Subject Line"
                  placeholder="e.g. Scheduled System Maintenance"
                />
                <FormTextField
                  name="messageBody"
                  label="Detailed Message Content"
                  multiline
                  rows={4}
                />
              </Stack>

              {/* --- ACTION BUTTON --- */}
              <Box sx={{ pt: 1 }}>
                <SubmitButton
                  title="Dispatch Notification"
                  loading={loading}
                  disabled={!isValid || !dirty}
                  startIcon={<SendIcon />}
                  fullWidth
                  sx={{ py: 1.5, fontWeight: 700 }}
                />
              </Box>
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateNotificationForm;
